import { Component, Inject, Prop } from 'vue-property-decorator';
import ApeironComponent from '../commons/apeiron-component.model';
import { Solucion } from '@/shared/model/solucion.model';
import { ISolicitud, Solicitud } from '@/shared/model/solicitud.model';
import SolicitudService from '@/entities/solicitud/solicitud.service';
import { EstadoSolicitud } from '@/shared/model/enumerations/estado-solicitud.model';
import { FiltroApoyoComplementario } from '@/shared/model/filtro.model';
import { TipoSolucion } from '@/shared/model/enumerations/tipo-solucion.model';
import ProgramaService from '@/shared/programa/programa.service';
import { Programa } from '@/shared/model/seguimiento/programa.model';

@Component
export default class FechasMovilidadComponent extends ApeironComponent {
  @Inject('solicitudService') public readonly solicitudService: () => SolicitudService;
  @Inject('programaService') private programaService: () => ProgramaService;

  @Prop({ required: false })
  public solucion: Solucion;

  @Prop({ required: false })
  public solicitud: Solicitud;

  public propOrder = 'estado';
  public reverse = false;
  private readonly MESES_PERMITIDOS = 12;
  private readonly MESES_PERMITIDOS_MAE_ESP = 6;

  public isFetching = false;
  form = this.solicitudModel;

  public periodoBecasValidas = false;
  public fechaInicioMovilidad: string;
  public fechaFinMovilidad: string;
  public inicioPeriodoMovilidad: string;
  public finPeriodoMovilidad: string;
  public duracionMovilidadMeses: number;
  public duracionMovilidadMesesDias: string;
  public duracionBecaMeses = 0;
  public pasosPreviosMovilidad = false;
  public pasosPreviosEspMed = false;
  public mesesAcumulados = 0;
  public maxMesesPermitidos = 0;
  public mesesRestantes = 0;
  public showNotaMeses = false;
  public mesesAculumadosPorGrado = new Map<string, number>();
  public programaDetalle = new Programa();

  public periodosDeApoyos: Array<{ id: string; periodo: string; indicadorActual: boolean }> = [];
  public mesesMaximosTotales = 0; // guarda el total de meses disponibles por beca y grado.
  public mesesDisponiblesSinEstaSolicitud = 0; // guarda los meses disponibles sin contar los meses indicados en está solicitud
  public mesesDisponiblesConEstaSolicitud = 0; // guarda los meses disponibles contando los meses indicados en está solicitud
  public mesesDisponiblesParaEstaSolicitud = 0; // guarda los meses disponibles para seleccionar en está solicitud
  public mesesAcumuladosSinEstaSolicitud = 0; // guarda los meses acumulados (formalizados) sin contar los de está solicitud
  public mesesAcumuladosConEstaSolicitud = 0; // guarda los meses acumulados (formalizados) contando los de está solicitud

  private readonly FECHA_PERIODO_MOVILIDAD_INICIO = `${new Date().getUTCFullYear()}-01-01`;
  private readonly FECHA_PERIODO_MOVILIDAD_FIN = `${new Date().getUTCFullYear()}-12-15`;
  private readonly FECHA_PERIODO_MOVILIDAD_ESP_MED_FIN = `${new Date().getUTCFullYear()}-12-31`;
  private readonly DOBLE_TITULACION = '5';
  private readonly CVE_DOBLE_TITULACION = 'DOBTIT';

  /**
   * Se inicializa model de form
   */
  private get solicitudModel() {
    return {
      solicitud: {
        beca: {
          periodoBeca: {
            fechaInicioBeca: '',
            fechaFinBeca: '',
          },
        },
        estadoActual: '',
      },
      tags: [],
    };
  }

  created() {
    this.form.tags = this.solucion.tags;
    this.getSolicitudVigente();
    this.findPrograma();
  }

  /**
   * Obtiene la solicitud vigente desde el servicio y actualiza el formulario con sus datos.
   * También calcula la duración de la beca en meses y busca apoyos complementarios asociados.
   * @async
   * @throws Lanza un error si `solicitudService().find` falla.
   */
  async getSolicitudVigente() {
    this.isFetching = true;

    try {
      const res = await this.solicitudService().find(this.solicitud.id);

      this.pasosPreviosMovilidad = !!res?.beca?.id && !!res?.apoyoSolicitado?.apoyo;
      this.pasosPreviosEspMed = !!res?.beca?.id;
      this.form.solicitud = { ...res, ...JSON.parse(JSON.stringify(res)) };
      this.isFetching = false;
      if (this.solicitud.beca) {
        this.duracionBecaMeses = this.getTotalMesesEntreFechas(
          this.convertirFecha(this.solicitud.beca?.periodoBeca.fechaInicioBeca),
          this.convertirFecha(this.solicitud.beca?.periodoBeca.fechaFinBeca)
        );
      }
      await this.findApoyosComplementarios(res.tags);
    } catch (error) {
      this.alertService().showHttpError(this, error.response ?? error);
    } finally {
      this.isFetching = false;
    }
  }

  public isCapturable(): boolean {
    const tags = Array.isArray(this.form?.tags) ? this.form.tags : [];
    if (tags.includes('MovilidadNacional')) {
      return this.pasosPreviosMovilidad;
    }
    if (tags.includes('MovilidadEspecialidadMedica')) {
      return this.pasosPreviosEspMed;
    }
    return false;
  }

  public isSaving(): boolean {
    return (
      this.solicitud.estado === EstadoSolicitud.EN_CAPTURA &&
      this.grantService().isSolicitante(this.solicitud, this.grantService().getUsername(), this.grantService().getUsercvu)
    );
  }

  /**
   * Valida y confirma el periodo de movilidad de la solicitud.
   *
   * @remarks
   * Esta función realiza las siguientes acciones:
   * 1. Convierte las fechas de inicio y fin de la beca y de la movilidad a objetos `Date`.
   * 2. Verifica que las fechas de movilidad sean lógicas (inicio antes del fin).
   * 3. Valida que las fechas de movilidad estén dentro del periodo de la beca.
   * 4. Comprueba que la movilidad no ocurra fuera del periodo vigente de movilidad.
   * 5. Calcula la duración de la movilidad en meses.
   * 6. Determina los meses acumulados y máximos permitidos según el grado del programa,
   *    la característica del programa (p. ej. doble titulación) o tags especiales (p. ej. MovilidadEspecialidadMedica).
   * 7. Muestra alertas de error o advertencia si alguna validación falla.
   * 8. Actualiza la solicitud con la duración de movilidad y guarda los cambios si todo es válido.
   *
   *
   * @returns `true` si las fechas son válidas y la solicitud se puede guardar, `false` en caso contrario.
   */
  public confirmaPeriodoChange() {
    this.periodoBecasValidas = true;

    const esEspecialidadMedica = this.solicitud?.tags.find(tag => tag === 'MovilidadEspecialidadMedica');
    const periodoFechaInicioBeca = this.convertirFecha(this.solicitud?.beca?.periodoBeca?.fechaInicioBeca);
    const periodoFechaFinBeca = this.convertirFecha(this.solicitud?.beca?.periodoBeca?.fechaFinBeca);

    const periodoFechaInicioMovilidadCaptura = this.convertirFecha(this.solicitud?.fechaInicioMovilidad);
    const periodoFechaFinMovilidad = this.convertirFecha(this.solicitud?.fechaFinMovilidad);

    const periodoEnCurso = this.convertirFecha(this.FECHA_PERIODO_MOVILIDAD_INICIO);

    const periodoFinCurso = this.convertirFecha(
      esEspecialidadMedica ? this.FECHA_PERIODO_MOVILIDAD_ESP_MED_FIN : this.FECHA_PERIODO_MOVILIDAD_FIN
    );

    const fechaActual = new Date();

    const fechaMinimaInicio = new Date(periodoFechaInicioBeca);
    fechaMinimaInicio.setMonth(fechaMinimaInicio.getMonth() + 6);

    if (fechaActual < fechaMinimaInicio) {
      this.alertService().showError(this, this.$t('apeironGwApp.fechasMovilidad.label.minimoSeisMesesBeca').toString());
      this.periodoBecasValidas = false;
      return false;
    }

    const fechaLimiteFin = new Date(fechaActual);

    if (periodoFechaFinBeca < fechaLimiteFin) {
      this.alertService().showError(this, this.$t('apeironGwApp.fechasMovilidad.label.finBecaVigente').toString());
      this.periodoBecasValidas = false;
      return false;
    }

    if (periodoFechaFinMovilidad <= periodoFechaInicioMovilidadCaptura) {
      this.alertService().showError(this, this.$t('apeironGwApp.fechasMovilidad.label.validaFechasMovilidad').toString());
      this.periodoBecasValidas = false;
      return false;
    }

    if (periodoFechaInicioBeca > periodoFechaInicioMovilidadCaptura || periodoFechaFinBeca < periodoFechaFinMovilidad) {
      this.alertService().showError(this, this.$t('apeironGwApp.fechasMovilidad.label.periodoValido').toString());
      this.periodoBecasValidas = false;
      return false;
    }

    if (periodoEnCurso > periodoFechaInicioMovilidadCaptura) {
      this.alertService().showError(
        this,
        this.$t('apeironGwApp.fechasMovilidad.label.periodoEnCurso', { anio: new Date().getUTCFullYear() }).toString()
      );
      this.periodoBecasValidas = false;
      return false;
    }

    if (periodoFinCurso < periodoFechaInicioMovilidadCaptura) {
      this.alertService().showError(this, 'apeironGwApp.fechasMovilidad.label.periodoFinCurso', {
        fecha: this.formatearFechaVista(periodoFinCurso),
      });
      this.periodoBecasValidas = false;
      return false;
    }

    if (this.periodoBecasValidas) {
      const caracteristicaPrograma = this.solicitud?.apoyoSolicitado?.apoyo;
      const duracionPrograma = this.programaDetalle?.planDeEstudios?.duracionOficial ?? 0;
      const grado = this.solicitud?.beca?.programa?.grado;

      if (caracteristicaPrograma === this.DOBLE_TITULACION) {
        this.maxMesesPermitidos = duracionPrograma / 2;
      } else {
        const gradosPermitidos = new Set(['MAESTRIA', 'ESPECIALIDAD']);

        this.maxMesesPermitidos =
          !esEspecialidadMedica && gradosPermitidos.has(grado) ? this.MESES_PERMITIDOS_MAE_ESP : this.MESES_PERMITIDOS;
      }

      // Esto se replica en otro método, si se actualiza este, también requere actualizar el otro. findApoyosComplementarios
      const keyAcumulados =
        caracteristicaPrograma === this.DOBLE_TITULACION
          ? this.CVE_DOBLE_TITULACION + '-' + grado
          : esEspecialidadMedica
          ? 'MovilidadEspecialidadMedica'
          : grado;
      this.mesesAcumulados = keyAcumulados ? this.mesesAculumadosPorGrado.get(keyAcumulados) ?? 0 : 0;
      const diasMesesMovilidad = this.calcularMesesDias(periodoFechaInicioMovilidadCaptura, periodoFechaFinMovilidad);
      this.solicitud.duracionMovilidadMesesDias = `${diasMesesMovilidad.meses} meses ${diasMesesMovilidad.dias} días`;
      const { meses, dias } = diasMesesMovilidad;
      const diferenciaEntreMeses = dias >= 15 ? meses + 1 : meses;
      this.duracionMovilidadMeses = diferenciaEntreMeses;
      this.solicitud.duracionMovilidadMeses = diferenciaEntreMeses;
      const inicioMovilidadMes = this.ajustarFechaPrimeroUltimo(periodoFechaInicioMovilidadCaptura, 1);
      this.solicitud.inicioPeriodoMovilidad = this.formatearFecha(inicioMovilidadMes);
      const finMovilidadMes = this.ajustarFechaPrimeroUltimo(periodoFechaFinMovilidad, 2);
      this.solicitud.finPeriodoMovilidad = this.formatearFecha(finMovilidadMes);
      this.solicitud.cronogramaActividades = [];

      const totalMesesSolicitados = diferenciaEntreMeses + this.mesesAcumulados;
      this.mesesRestantes = this.maxMesesPermitidos - this.mesesAcumulados;

      if (totalMesesSolicitados > this.maxMesesPermitidos) {
        this.showNotaMeses = true;
        this.alertService().showWarning(
          this,
          this.$t('apeironGwApp.fechasMovilidad.label.duracionMaxMovilidad', {
            max: this.maxMesesPermitidos,
          }).toString()
        );
        this.periodoBecasValidas = false;
        this.solicitud.fechaInicioMovilidad = '';
        this.solicitud.fechaFinMovilidad = '';
        return false;
      }

      this.save(this.solicitud);
      return true;
    }
  }

  /**
   * Convierte un string de fecha en un objeto Date.
   */
  public convertirFecha(fechaString?: string): Date {
    if (fechaString) {
      if (fechaString.includes('-')) {
        const [year, month, day] = fechaString.split('-').map(val => parseInt(val));
        return new Date(year, month - 1, day);
      } else {
        const [day, month, year] = fechaString.split('/').map(val => parseInt(val));
        return new Date(year, month - 1, day);
      }
    }
  }

  private esPrimerDiaDelMes(fecha: Date): boolean {
    return fecha.getDate() === 1;
  }

  private esUltimoDiaDelMes(fecha: Date): boolean {
    const siguienteDia = new Date(fecha);
    siguienteDia.setDate(siguienteDia.getDate() + 1);
    return siguienteDia.getDate() === 1; // Si el siguiente día es el primero del mes, entonces `fecha` es el último día del mes anterior
  }

  private getTotalMesesEntreFechas(fechaInicio: Date, fechaFin: Date): number {
    //Solo para comparar entre 2 fechas cuando la fin es ultimo dia del mes:

    let meses = (fechaFin.getFullYear() - fechaInicio.getFullYear()) * 12 + fechaFin.getMonth() - fechaInicio.getMonth();
    // Primer escenario
    if (this.esPrimerDiaDelMes(fechaInicio)) {
      meses++;
    }
    // Segundo escenario
    else if (this.esUltimoDiaDelMes(fechaInicio) && this.esPrimerDiaDelMes(fechaFin)) {
      meses++;
    }
    // Tercer escenario
    else {
      meses++;
    }
    return meses;
  }

  /**
   * Obtiene y procesa los apoyos complementarios asociados a los tags
   * de la solicitud actual del solicitante.
   *
   * @description
   * Consulta las solicitudes de apoyo complementario previamente registradas
   * para el CVU del solicitante y calcula los meses de movilidad acumulados,
   * organizándolos en el mapa `mesesAculumadosPorGrado`.
   *
   * @businessRules
   * - Se excluye explícitamente el tag `'ApoyosNacionales'` del proceso.
   * - Si los tags incluyen `'MovilidadEspecialidadMedica'`:
   *   - Se suman todos los valores de `duracionMovilidadMeses`.
   *   - El total se almacena bajo la clave `'MovilidadEspecialidadMedica'`.
   *
   * - Si los tags incluyen `'MovilidadNacional'`:
   *   - Las solicitudes se agrupan por `grado` del programa.
   *   - Si el apoyo corresponde a doble titulación se agrupa por
   *       `CVE_DOBLE_TITULACION + '-' + grado`
   *   - En caso contrario, la clave será únicamente el `grado`.
   *   - Los meses se acumulan por cada clave.
   *
   * - Si no hay grado o clave válida, la solicitud no se considera en la acumulación.
   *
   * @internal
   * - Este método actualiza directamente la propiedad
   *   `mesesAculumadosPorGrado`.
   * - No retorna datos; su efecto es colateral (side-effect).
   * - Está diseñado para ejecutarse antes de validar límites máximos
   *   de movilidad en otros métodos del flujo.
   *
   * @param tags - Lista opcional de identificadores que determinan
   * los tipos de apoyos complementarios a consultar.
   *
   * @returns `Promise<void>` que se resuelve cuando termina el procesamiento.
   */
  private async findApoyosComplementarios(tags?: Array<string>) {
    try {
      tags = tags?.filter(tag => tag !== 'ApoyosNacionales') ?? [];
      const arraySolicitudes = await this.getSolicitudApoyoComplementario(this.solicitud.solicitante.cvu, tags);

      this.periodosDeApoyos = arraySolicitudes.map(solicitud => ({
        id: solicitud.id ?? '++',
        periodo: `(${solicitud.fechaInicioMovilidad} - ${solicitud.fechaFinMovilidad}) ${solicitud.duracionMovilidadMeses} meses - ${solicitud.apoyoSolicitado.nombre}`,
        indicadorActual: this.solicitud.id == solicitud.id,
      }));

      if (tags?.includes('MovilidadEspecialidadMedica')) {
        const totalMeses = arraySolicitudes.reduce((total, s) => total + (s.duracionMovilidadMeses ?? 0), 0);
        this.mesesAculumadosPorGrado.set('MovilidadEspecialidadMedica', totalMeses);
      } else if (tags?.includes('MovilidadNacional')) {
        this.mesesAculumadosPorGrado = arraySolicitudes.reduce((map, s) => {
          const meses = s.duracionMovilidadMeses ?? 0;
          const grado = s.beca.programa?.grado;
          const esDobleTitulacion = s.apoyoSolicitado?.apoyo === this.DOBLE_TITULACION;
          const key = esDobleTitulacion ? this.CVE_DOBLE_TITULACION + '-' + grado : grado;
          if (!key) return map;
          const acumulado = map.get(key) ?? 0;
          map.set(key, acumulado + meses);
          return map;
        }, new Map<string, number>());
      }

      // parte de está sección se mantiene junto con la de la función confirmaPeriodoChange
      const esEspecialidadMedica = (this.solicitud?.tags ?? []).find(tag => tag === 'MovilidadEspecialidadMedica');
      const periodoFechaInicioMovilidadCaptura = this.convertirFecha(this.solicitud?.fechaInicioMovilidad);
      const periodoFechaFinMovilidad = this.convertirFecha(this.solicitud?.fechaFinMovilidad);
      const caracteristicaPrograma = this.solicitud?.apoyoSolicitado?.apoyo;
      const duracionPrograma = this.programaDetalle?.planDeEstudios?.duracionOficial ?? 0;
      const grado = this.solicitud?.beca?.programa?.grado ?? '++';
      let maxMesesPermitidos = 0;

      if (arraySolicitudes.filter(solicitud => solicitud.id == this.solicitud.id).length == 0) {
        this.periodosDeApoyos.push({
          id: this.solicitud.id ?? '--',
          periodo: `(${this.solicitud.fechaInicioMovilidad} - ${this.solicitud.fechaFinMovilidad}) ${
            this.solicitud.duracionMovilidadMeses
          } meses - ${this.solicitud.apoyoSolicitado?.nombre ?? ''}`,
          indicadorActual: true,
        });
      }

      if (caracteristicaPrograma === this.DOBLE_TITULACION) {
        maxMesesPermitidos = duracionPrograma / 2;
      } else {
        const gradosPermitidos = new Set(['MAESTRIA', 'ESPECIALIDAD']);
        maxMesesPermitidos = !esEspecialidadMedica && gradosPermitidos.has(grado) ? this.MESES_PERMITIDOS_MAE_ESP : this.MESES_PERMITIDOS;
      }

      const keyAcumulados =
        caracteristicaPrograma === this.DOBLE_TITULACION
          ? this.CVE_DOBLE_TITULACION + '-' + grado
          : esEspecialidadMedica
          ? 'MovilidadEspecialidadMedica'
          : grado;
      const mesesAcumulados = keyAcumulados ? this.mesesAculumadosPorGrado.get(keyAcumulados) ?? 0 : 0;
      const diasMesesMovilidad = this.calcularMesesDias(periodoFechaInicioMovilidadCaptura, periodoFechaFinMovilidad);
      const { meses, dias } = diasMesesMovilidad ?? { meses: 0, dias: 0 };
      const duracionMovilidadMeses = dias >= 15 ? meses + 1 : meses;

      this.mesesMaximosTotales = maxMesesPermitidos;
      this.mesesDisponiblesParaEstaSolicitud = maxMesesPermitidos - mesesAcumulados;
      this.mesesAcumuladosSinEstaSolicitud = mesesAcumulados;
      this.mesesAcumuladosConEstaSolicitud =
        (arraySolicitudes.filter(solicitud => solicitud.id == this.solicitud.id).length == 0 ? duracionMovilidadMeses : 0) +
        mesesAcumulados;
      this.mesesDisponiblesSinEstaSolicitud = maxMesesPermitidos - mesesAcumulados;
      this.mesesDisponiblesConEstaSolicitud =
        maxMesesPermitidos -
        ((arraySolicitudes.filter(solicitud => solicitud.id == this.solicitud.id).length == 0 ? duracionMovilidadMeses : 0) +
          mesesAcumulados);
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
    } finally {
    }
  }

  /**
   * Recupera las solicitudes de apoyo complementario asociadas a un CVU y opcionalmente filtradas por tags.
   *
   * @remarks
   * Esta función:
   * 1. Crea un filtro de solicitudes de tipo `APOYOS_COMPLEMENTARIOS_BN` y estado `FORMALIZADA`.
   * 2. Aplica los tags y el CVU proporcionados al filtro.
   * 3. Realiza una consulta paginada usando `solicitudService().retrieve`.
   * 4. Maneja errores de HTTP mostrando alertas y relanzando la excepción.
   *
   * @param cvu - CVU del solicitante para filtrar las solicitudes .
   * @param tags - Lista de tags para filtrar las solicitudes .
   * @returns Un `Promise` que se resuelve con un array de `ISolicitud`.
   *
   * @throws Lanza un error si la consulta HTTP falla.
   */
  public async getSolicitudApoyoComplementario(cvu: string, tags?: Array<string>): Promise<Array<ISolicitud>> {
    const filtroSolicitud = new FiltroApoyoComplementario();
    filtroSolicitud.estadosSolicitud = [EstadoSolicitud.FORMALIZADA];
    filtroSolicitud.tipoSolucion = TipoSolucion.APOYOS_COMPLEMENTARIOS_BN;
    filtroSolicitud.tags = tags;
    filtroSolicitud.cvuSolicitante = cvu;
    const paginationQuery = {
      page: 0,
      size: 50,
      sort: this.sort(),
      filter: filtroSolicitud,
    };
    try {
      const response = await this.solicitudService().consultarSolicitudes(filtroSolicitud.tipoSolucion, paginationQuery);
      return response.data;
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }

  /**
   * Genera un array de criterios de ordenamiento para las consultas.
   */
  public sort(): Array<any> {
    const result = [this.propOrder + ',' + (this.reverse ? 'desc' : 'asc')];
    if (this.propOrder !== 'id') {
      result.push('id');
    }
    return result;
  }

  /**
   * Mensaje que muestra información de los apoyos solicitados en la movilidad.
   *
   * @remarks
   * Este getter genera un mensaje traducido que indica:
   * - Los meses acumulados del solicitante.
   * - El máximo de meses permitidos.
   * - Los meses restantes disponibles.
   *
   * Se utiliza la función de traducción `this.$t` con los valores actuales.
   *
   * @returns Un string traducido con los datos de meses acumulados y límites.
   */
  get showMessage() {
    return this.$t('apeironGwApp.fechasMovilidad.notas.apoyosSolicitados', {
      mesesAcumulados: this.mesesAcumulados,
      maxMesesPermitidos: this.maxMesesPermitidos,
      mesesRestantes: this.mesesRestantes,
    });
  }

  /**
   * Busca los detalles del programa asociado a la beca de la solicitud actual.
   */
  public findPrograma(): void {
    this.isFetching = true;
    this.programaService()
      .find(this.solicitud?.beca?.programa?.clave)
      .then(
        programa => {
          if (programa != null) {
            this.programaDetalle = programa;
          }
          this.isFetching = false;
        },
        err => {
          this.isFetching = false;
          this.alertService().showHttpError(this, err.response);
        }
      );
  }

  /**
   * Ajusta las fechas de captura al primero y último día del mes
   * @param fecha la fecha de fin.
   * @returns fecha modificada
   */
  public ajustarFechaPrimeroUltimo(fecha: Date, ajuste: 1 | 2): Date {
    //primero de cada mes
    if (ajuste === 1) {
      return new Date(fecha.getFullYear(), fecha.getMonth(), 1);
    }
    //fin de cada mes
    if (ajuste === 2) {
      return new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0);
    }
    return fecha;
  }

  /**
   * Convierte fechas de tipo Date en String
   * @param fecha la fecha de fin.
   * @returns fecha string
   */
  formatearFecha(fecha: Date): string {
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, '0');
    const day = String(fecha.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  /**
   * Calcula la duración en meses y días de la movilidad
   * @param fechaInicio la fecha de fin.
   * @param fechaFin la fecha de fin.
   * @returns Meses y días de la movilidad o warning en caso de no cumplir la condición.
   */
  calcularMesesDias(fechaInicio: Date, fechaFin: Date) {
    const anios = fechaFin.getFullYear() - fechaInicio.getFullYear();
    let meses = fechaFin.getMonth() - fechaInicio.getMonth();
    let dias = fechaFin.getDate() - fechaInicio.getDate() + 1;

    // convertir años a meses
    meses += anios * 12;

    // ajustar si los días son negativos
    if (dias < 0) {
      meses -= 1;

      const ultimoMes = new Date(fechaFin.getFullYear(), fechaFin.getMonth(), 0);
      dias += ultimoMes.getDate();
    }

    // validación mínima
    if (meses < 0 || (meses === 0 && dias < 28)) {
      this.alertService().showWarning(
        this,
        this.$t('apeironGwApp.fechasMovilidad.label.periodoMinMovilidad', {
          max: this.maxMesesPermitidos,
        }).toString()
      );
      return;
    }

    return {
      meses,
      dias,
    };
  }

  /**
   * Convierte fechas de tipo Date en String en formato DD/MM/YYYY
   * @param fecha la fecha de fin.
   * @returns fecha string
   */
  formatearFechaVista(fecha: Date): string {
    const day = String(fecha.getDate()).padStart(2, '0');
    const month = String(fecha.getMonth() + 1).padStart(2, '0');
    const year = fecha.getFullYear();

    return `${day}/${month}/${year}`;
  }
}
